// frontend/src/EvaluationInterface.tsx - UPDATED with Buttons and Initial Save Fix

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

interface EvaluationInterfaceProps {
  processId: number;
  supplierId: number;
  evaluatorId: number;
  userRole?: string;
  onBack: () => void;
  onComplete: () => void;
}

// --- Backend API Response Interfaces (aligned with your FastAPI models) ---
interface ProcessResponse {
  id: number;
  name: string;
  description: string | null;
  ito_reference: string | null;
  status: string;
  score_scale: string;
  created_at: string;
  updated_at: string;
}

interface MandatoryRequirementResponse {
  id: number;
  description: string;
  order_index: number;
}

interface SubCriterionResponse {
  id: number;
  name: string;
  description: string | null;
  weight_percentage: number;
  order_index: number;
}

interface MainCriterionResponse {
  id: number;
  name: string;
  description: string | null;
  weight_percentage: number;
  order_index: number;
  sub_criteria: SubCriterionResponse[];
}

interface SupplierResponse {
  id: number;
  name: string;
  abn_acn: string;
  total_offered_price: number | null;
  created_at: string;
}

interface CompleteProcessDataResponse {
  process: ProcessResponse;
  mandatory_requirements: MandatoryRequirementResponse[];
  main_criteria: MainCriterionResponse[];
  suppliers: SupplierResponse[];
}

// --- Local State Interfaces for Evaluation Data ---
interface MandatoryEvaluation {
  requirement_id: number;
  is_compliant: boolean;
  comments: string;
}

interface SubCriterionEvaluation {
  sub_criterion_id: number;
  score: number | null;
  comments: string;
}

interface ScoreDefinition {
  score: number;
  title: string;
  description: string;
}

// --- Backend Submission Interfaces (for POST /api/evaluations/) ---
interface MandatoryEvaluationSubmission {
    requirement_id: number;
    is_compliant: boolean;
    comments?: string | null;
}

interface SubCriterionScoreSubmission {
    sub_criterion_id: number;
    score: number;
    comments: string; // MANDATORY: Comments are required for all evaluations
}

interface EvaluationSubmissionPayload {
    process_id: number;
    supplier_id: number;
    evaluator_id: number;
    mandatory_evaluations: MandatoryEvaluationSubmission[];
    sub_criterion_scores: SubCriterionScoreSubmission[];
    is_complete: boolean;
}

// Backend Response structure for GET /api/evaluations/{process_id}/{supplier_id}/{evaluator_id}
interface ExistingEvaluationResponse {
    evaluation_result: {
        id: number;
        process_id: number;
        supplier_id: number;
        evaluator_id: number;
        mandatory_requirements_passed: number;
        total_score: number;
        vfm_score: number | null;
        is_complete: boolean;
        completed_at: string | null;
        created_at: string;
        updated_at: string;
    };
    mandatory_evaluations: MandatoryEvaluationSubmission[];
    sub_criterion_scores: SubCriterionScoreSubmission[];
    exists: boolean;
}

const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)',
      padding: 'var(--spacing-lg)'
    },
    contentWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    pageTitle: {
      fontSize: 'var(--font-size-headline-large)',
      fontWeight: 'var(--font-weight-headline)',
      color: 'var(--color-text-on-gradient)',
      marginBottom: 'var(--spacing-xl)',
      textAlign: 'center' as 'center'
    },
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--border-radius-lg)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      marginBottom: 'var(--spacing-xl)'
    },
    button: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      background: 'var(--color-primary)',
      color: 'white',
      border: 'none',
      borderRadius: 'var(--border-radius-md)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    buttonSecondary: {
        background: 'white',
        color: '#4f46e5',
        border: '2px solid #4f46e5',
    },
    // NEW styles for Compliant/Non-Compliant buttons
    compliantButton: {
        padding: '0.5rem 1rem',
        border: '2px solid #d1d5db',
        borderRadius: '8px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        flex: 1,
    },
    compliantButtonActive: {
        background: '#10b981',
        color: 'white',
        borderColor: '#059669',
    },
    nonCompliantButtonActive: {
        background: '#ef4444',
        color: 'white',
        borderColor: '#dc2626',
    },
    buttonInactive: {
        background: '#f9fafb',
        color: '#6b7280',
    },
    input: {
      width: '100%',
      padding: '16px 20px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 0.2s',
      outline: 'none',
      boxSizing: 'border-box' as const
    },
    label: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem',
      display: 'block'
    },
};

const handleButtonHover = (e: React.MouseEvent<HTMLButtonElement>, enter: boolean) => {
    e.currentTarget.style.transform = enter ? 'translateY(-2px)' : 'translateY(0)';
    e.currentTarget.style.boxShadow = enter ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none';
};

const EvaluationInterface: React.FC<EvaluationInterfaceProps> = ({
  processId,
  supplierId,
  evaluatorId,
  userRole,
  onBack,
  onComplete,
}) => {

  // State for fetched process data
  const [process, setProcess] = useState<ProcessResponse | null>(null);
  const [supplier, setSupplier] = useState<SupplierResponse | null>(null);
  const [mandatoryRequirements, setMandatoryRequirements] = useState<MandatoryRequirementResponse[]>([]);
  const [mainCriteria, setMainCriteria] = useState<MainCriterionResponse[]>([]);

  // State for evaluation progress
  const [currentStep, setCurrentStep] = useState<'mandatory' | 'evaluation'>('mandatory');
  const [currentCriterionIndex, setCurrentCriterionIndex] = useState(0);
  const [currentSubCriterionIndex, setCurrentSubCriterionIndex] = useState(0);
  const [expandedCriteria, setExpandedCriteria] = useState<Set<number>>(new Set([0])); // First criterion expanded by default
  const [expandedSubCriteria, setExpandedSubCriteria] = useState<Set<string>>(new Set()); // Track expanded sub-criteria by ID

  // Evaluation data (to be initialized from fetched data and updated for submission)
  const [mandatoryEvaluations, setMandatoryEvaluations] = useState<MandatoryEvaluation[]>([]);
  const [subCriterionEvaluations, setSubCriterionEvaluations] = useState<SubCriterionEvaluation[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const scoreDefinitions_0_5: ScoreDefinition[] = [
    { score: 0, title: "Non-compliant / No response", description: "The Offer does not address the criterion, or the response is missing, irrelevant, or wholly inadequate." },
    { score: 1, title: "Poor", description: "The Offer demonstrates a very limited understanding of the criterion, with significant gaps or deficiencies." },
    { score: 2, title: "Below expectations", description: "The Offer addresses the criterion to some extent but lacks sufficient detail, clarity, or evidence of capability." },
    { score: 3, title: "Meets expectations", description: "The Offer adequately addresses the criterion, providing sufficient detail and evidence to meet the requirements." },
    { score: 4, title: "Exceeds expectations", description: "The Offer exceeds the basic requirements of the criterion, offering clear, detailed, and well-supported responses." },
    { score: 5, title: "Outstanding", description: "The Offer is exceptional, providing innovative, comprehensive, and highly relevant responses that add significant value." }
  ];

  const scoreDefinitions_0_10: ScoreDefinition[] = [
    { score: 0, title: "Non-compliant/No response", description: "The Offer fails to provide adequate evidence to satisfy the evaluation criterion, or the Offer does not provide a response to the evaluation criterion." },
    { score: 1, title: "Extremely Poor requirements", description: "The Offer demonstrates a complete misunderstanding of the evaluation criterion, does not meet the requirements. Or the Offer demonstrates that the Supplier has an extremely limited capability such that the Evaluation Panel would have no confidence that the Supplier would be able to satisfactorily complete the contract requirements covered by the criterion. And/or - Critical Weakness / risk." },
    { score: 2, title: "Very Poor requirements", description: "The Offer demonstrates a limited understanding of the evaluation criterion, with significant gaps or deficiencies. Or the Offer demonstrates that the Supplier has a very limited capability such that the Evaluation Panel would have no confidence that the Supplier would be able to satisfactorily complete the contract requirements covered by the criterion. Intermediate score: slightly superior to 1 or slightly inferior to 3." },
    { score: 3, title: "Poor requirements", description: "The Offer does not demonstrate an acceptable level of understanding of the evaluation criterion and requirements and has significant gaps and deficiencies. Or the Offer demonstrates that the Supplier has such a limited capability such that the Evaluation Panel would have no confidence that the Supplier would be able to satisfactorily complete the contract requirements covered by the criterion. And/or - High weakness / risk." },
    { score: 4, title: "Below Average requirements", description: "The Offer demonstrates a limited understanding of the evaluation criterion to some extent but lacks sufficient detail and clarity. Or the Offer demonstrates that the Supplier has limited evidence of capability such that the Evaluation Panel would be not reasonably confident that the Supplier would be able to satisfactorily complete the contract requirements covered by the criterion. Intermediate score: slightly superior to 3 or slightly inferior to 5." },
    { score: 5, title: "Meets minimum requirements", description: "The Offer demonstrates a reasonable understanding of the evaluation criterion or demonstrates that the Supplier can provide the required capability, however the Evaluation Panel note that performance management would be required from the Customer in order for the Supplier to meet requirements. As such, the Evaluation Panel is somewhat confident that the Supplier would meet the contract requirements covered by the criterion. Demonstrates a reasonable understanding of the requirement, however some minor deficiencies. And/or - Medium weakness / risk." },
    { score: 6, title: "Slightly above minimum requirements", description: "The Offer demonstrates a good understanding of the evaluation criterion and provides additional detail or evidence beyond the minimum requirements. Or demonstrates that the Supplier can provide the required capability such that the Evaluation Panel is confident that the Supplier would meet the contract requirements covered by the criterion. And/or - Low weakness / risk." },
    { score: 7, title: "Above minimum requirements and some of the response exceeds requirements", description: "The Offer demonstrates a good, developed understanding of the evaluation criterion and provides additional detail or evidence beyond the minimum requirements, with some elements exceeding requirements. Or demonstrates that the Supplier can provide the required capability such that the Evaluation Panel is confident that the Supplier would meet the contract requirements covered by the criterion whilst adding some value. Intermediate score: slightly superior to 6 or slightly inferior to 8." },
    { score: 8, title: "Majority of the response exceeds requirements", description: "The Offer demonstrates a thorough understanding of the evaluation criterion, offering clear, detailed and well-supported responses, with a majority of the response exceeding requirements. The Offer demonstrates that the Supplier has strong capability to successfully meet the contract requirements and provide some additional value and or innovation. And/or - Very low weakness / risk." },
    { score: 9, "title": "The entire response exceeds requirements", "description": "The Offer demonstrates a thorough understanding of the evaluation criterion, offering clear, detailed and well-supported responses, with the entire response exceeding requirements. The Offer demonstrates that the Supplier has very strong capability to successfully meet the contract requirements and provide additional value and or innovation. Intermediate score: slightly superior to 8 or slightly inferior to 10." },
    { score: 10, title: "The entire response exceeds requirements in an exceptional and outstanding manner", description: "The Offer demonstrates an exceptional understanding of the evaluation criterion, providing innovative, comprehensive and highly relevant responses. The Offer demonstrates that the Supplier exceeds, in an exceptional manner, the necessary capability to successfully meet the contract requirements and provide significant value and/or innovation. And/or - No weakness / risk." }
  ];

  // Utility to show notifications
  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // --- Data Loading Effect ---
  useEffect(() => {
    const loadEvaluationData = async () => {
      setIsLoading(true);
      try {
        const token = Cookies.get('access_token');
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

        const processDataResponse = await axios.get<CompleteProcessDataResponse>(
          `${API_URL}/api/processes/${processId}/complete-data`,
          authHeaders
        );
        const data = processDataResponse.data;

        setProcess(data.process);
        setMandatoryRequirements(data.mandatory_requirements || []);
        
        // Backend now ensures all main criteria have sub-criteria (synthetic ones if needed)
        const processedCriteria = data.main_criteria || [];
        
        // DEBUG: Log the criteria structure to see what's wrong
        processedCriteria.forEach((criterion, index) => {
          if (criterion.sub_criteria) {
          } else {
          }
        });
        
        setMainCriteria(processedCriteria);

        const targetSupplier = data.suppliers.find(s => s.id === supplierId);
        if (!targetSupplier) {
          showNotification('error', `Supplier with ID ${supplierId} not found for this process.`);
          setIsLoading(false);
          onBack();
          return;
        }
        setSupplier(targetSupplier);

        let loadedMandatory: MandatoryEvaluation[] = [];
        let loadedSubCriteria: SubCriterionEvaluation[] = [];
        let resumeStep: 'mandatory' | 'evaluation' = 'mandatory';
        let wasNewEvaluationCreated = false; // FIX: Flag to track if we're creating a new record

        try {
          const existingEvaluationResponse = await axios.get<ExistingEvaluationResponse>(
            `${API_URL}/api/evaluations/${processId}/${supplierId}/${evaluatorId}`,
            authHeaders
          );
          const existingData = existingEvaluationResponse.data;

          if (existingData.exists && existingData.evaluation_result) {
            loadedMandatory = data.mandatory_requirements.map(req => {
              const existing = existingData.mandatory_evaluations.find(
                (evalItem: MandatoryEvaluationSubmission) => evalItem.requirement_id === req.id
              );
              return {
                requirement_id: req.id,
                is_compliant: existing ? existing.is_compliant : true,
                comments: (existing && existing.comments !== null && existing.comments !== undefined)
                          ? existing.comments
                          : '',
              };
            });

            processedCriteria.forEach(criterion => {
              criterion.sub_criteria.forEach(sub => {
                // For main criteria without sub-criteria, check both the main criterion ID and old synthetic ID
                let existing = existingData.sub_criterion_scores.find(
                  (scoreItem: SubCriterionScoreSubmission) => scoreItem.sub_criterion_id === sub.id
                );
                
                // If not found and this is a synthetic sub-criterion (main criterion with no real sub-criteria), 
                // also check for old synthetic ID pattern
                if (!existing && criterion.sub_criteria.length === 1 && sub.id === criterion.id) {
                  const oldSyntheticId = criterion.id * 1000;
                  existing = existingData.sub_criterion_scores.find(
                    (scoreItem: SubCriterionScoreSubmission) => scoreItem.sub_criterion_id === oldSyntheticId
                  );
                  if (existing) {
                  }
                }
                
                loadedSubCriteria.push({
                  sub_criterion_id: sub.id, // Always use the correct ID
                  score: existing ? existing.score : null,
                  comments: (existing && existing.comments !== null && existing.comments !== undefined)
                            ? existing.comments
                            : '',
                });
              });
            });

            if (existingData.evaluation_result.is_complete) {
                showNotification('success', 'Evaluation already completed for this supplier. Viewing saved data.');
                resumeStep = 'evaluation';
            } else {
                showNotification('success', 'Existing evaluation data loaded. Resuming progress.');

                const allMandatoryCompliantInLoaded = loadedMandatory.every(m => m.is_compliant);
                const hasAnySubCriterionScore = loadedSubCriteria.some(s => s.score !== null || s.comments !== '');

                if (allMandatoryCompliantInLoaded && hasAnySubCriterionScore) {
                    resumeStep = 'evaluation';
                    let lastEvaluatedCriterionIndex = 0;
                    let lastEvaluatedSubCriterionIndex = 0;

                    for (let i = 0; i < processedCriteria.length; i++) {
                        const criterion = processedCriteria[i];
                        for (let j = 0; j < criterion.sub_criteria.length; j++) {
                            const sub = criterion.sub_criteria[j];
                            const evalItem = loadedSubCriteria.find(lsc => lsc.sub_criterion_id === sub.id);
                            if (evalItem && (evalItem.score !== null || evalItem.comments !== '')) {
                                lastEvaluatedCriterionIndex = i;
                                lastEvaluatedSubCriterionIndex = j;
                            }
                        }
                    }
                    setCurrentCriterionIndex(lastEvaluatedCriterionIndex);
                    setCurrentSubCriterionIndex(lastEvaluatedSubCriterionIndex);
                } else {
                    resumeStep = 'mandatory';
                }
            }
          }
        } catch (error: any) {
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            wasNewEvaluationCreated = true; // FIX: Set the flag here
          } else {
            console.error('Error fetching existing evaluation:', error);
            showNotification('error', 'Failed to load existing evaluation data.');
          }
        }

        if (loadedMandatory.length === 0) {
            loadedMandatory = data.mandatory_requirements.map(req => ({
                requirement_id: req.id,
                is_compliant: true,
                comments: '',
            }));
        }
        setMandatoryEvaluations(loadedMandatory);

        if (loadedSubCriteria.length === 0) {
            processedCriteria.forEach(criterion => {
                if (criterion.sub_criteria && criterion.sub_criteria.length > 0) {
                    criterion.sub_criteria.forEach(sub => {
                        loadedSubCriteria.push({
                            sub_criterion_id: sub.id,
                            score: null,
                            comments: '',
                        });
                    });
                } else {
                }
            });
        }
        setSubCriterionEvaluations(loadedSubCriteria);

        // FIX: If a new evaluation was created, perform an initial save with default values
        if (wasNewEvaluationCreated) {
            try {
                const token = Cookies.get('access_token');
                const initialPayload: EvaluationSubmissionPayload = {
                    process_id: data.process.id,
                    supplier_id: targetSupplier.id,
                    evaluator_id: evaluatorId,
                    mandatory_evaluations: loadedMandatory.map(e => ({
                        requirement_id: e.requirement_id,
                        is_compliant: e.is_compliant,
                        comments: e.comments || null
                    })),
                    sub_criterion_scores: loadedSubCriteria
                      .filter(e => e.score !== null && e.comments && e.comments.trim() !== "")
                      .map(e => ({
                        sub_criterion_id: e.sub_criterion_id,
                        score: e.score!,
                        comments: e.comments!
                      })),
                    is_complete: false,
                };

                await axios.post(`${API_URL}/api/evaluations/`, initialPayload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showNotification('success', 'New evaluation created with default values.');
                setLastSaved(new Date());
            } catch (saveError) {
                console.error('Failed to perform initial save:', saveError);
                showNotification('error', 'Could not save initial evaluation state.');
            }
        }

        setCurrentStep(resumeStep);

      } catch (error) {
        console.error('Failed to load process or supplier data for evaluation:', error);
        showNotification('error', 'Failed to load evaluation data.');
        onBack();
      } finally {
        setIsLoading(false);
      }
    };

    loadEvaluationData();
  }, [processId, supplierId, evaluatorId, onBack, showNotification, userRole]);


  // --- Save Logic (Centralized) ---
  const sendEvaluationToBackend = useCallback(async (isComplete: boolean) => {
    if (!process || !supplier || !evaluatorId) {
      showNotification('error', 'Cannot save: process, supplier, or evaluator data missing.');
      return false;
    }

    setIsSaving(true);
    try {
      const token = Cookies.get('access_token');
      
      // ULTRA-STRICT VALIDATION: Build a whitelist of ONLY known valid IDs
      const knownValidIds = new Set<number>();
      
      mainCriteria.forEach(criterion => {
        
        if (criterion.sub_criteria.length === 1 && criterion.sub_criteria[0].id === criterion.id) {
          // This is a main criterion without real sub-criteria
          knownValidIds.add(criterion.id);
        } else {
          // This has real sub-criteria
          criterion.sub_criteria.forEach(sub => {
            if (sub.id !== criterion.id) { // Only real sub-criteria, not synthetic ones
              knownValidIds.add(sub.id);
            }
          });
        }
      });
      
      
      // STRICT FILTERING: Only allow IDs that are in our whitelist
      const safeSubCriterionScores = subCriterionEvaluations
        .filter(e => e.score !== null && e.comments && e.comments.trim() !== "")
        .map(evaluation => {
          if (knownValidIds.has(evaluation.sub_criterion_id)) {
            return {
              sub_criterion_id: evaluation.sub_criterion_id,
              score: evaluation.score!,
              comments: evaluation.comments!
            };
          } else {
            console.warn(`🚨 REJECTED ID: ${evaluation.sub_criterion_id} (not in whitelist)`);
            return null;
          }
        })
        .filter(score => score !== null) as SubCriterionScoreSubmission[];
      

      const payload: EvaluationSubmissionPayload = {
        process_id: process.id,
        supplier_id: supplier.id,
        evaluator_id: evaluatorId,
        mandatory_evaluations: mandatoryEvaluations.map(e => ({
          requirement_id: e.requirement_id,
          is_compliant: e.is_compliant,
          comments: e.comments || null
        })),
        sub_criterion_scores: safeSubCriterionScores,
        is_complete: isComplete,
      };

      await axios.post(`${API_URL}/api/evaluations/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLastSaved(new Date());
      if (isComplete) {
        // Only show finalization message on explicit complete action
      } else {
        showNotification('success', `Progress saved successfully!`);
      }
      return true;

    } catch (error: any) {
      console.error('Failed to save evaluation:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save evaluation.';
      showNotification('error', errorMessage);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [process, supplier, evaluatorId, mandatoryEvaluations, subCriterionEvaluations, showNotification]);


  // Auto-save every 30 seconds
  useEffect(() => {
    if (!isLoading && process && supplier && (currentStep === 'mandatory' || currentStep === 'evaluation')) {
      const interval = setInterval(() => {
        // Only auto-save if there are evaluations to save (prevent 422 errors)
        const hasValidEvaluations = subCriterionEvaluations.some(e => e.score !== null || (e.comments && e.comments.trim() !== ''));
        if (hasValidEvaluations || mandatoryEvaluations.some(e => e.comments && e.comments.trim() !== '')) {
          sendEvaluationToBackend(false);
        } else {
        }
      }, 30000);
      return () => clearInterval(interval);
    }
    return () => {};
  }, [isLoading, process, supplier, currentStep, sendEvaluationToBackend, subCriterionEvaluations, mandatoryEvaluations]);


  // --- Handlers for UI Interactions ---
  const updateMandatoryEvaluation = (requirementId: number, field: keyof MandatoryEvaluation, value: any) => {
    setMandatoryEvaluations(prev =>
      prev.map(evaluation =>
        evaluation.requirement_id === requirementId
          ? { ...evaluation, [field]: value }
          : evaluation
      )
    );
  };

  const updateSubCriterionEvaluation = useCallback((subCriterionId: number, field: keyof SubCriterionEvaluation, value: any) => {
    setSubCriterionEvaluations(prev =>
      prev.map(evaluation =>
        evaluation.sub_criterion_id === subCriterionId
          ? { ...evaluation, [field]: value }
          : evaluation
      )
    );
    
    // Auto-save when score is updated
    if (field === 'score') {
      setTimeout(() => sendEvaluationToBackend(false), 500); // Debounce save by 500ms
    }
  }, [sendEvaluationToBackend]);

  const saveMandatoryEvaluations = async () => {
    // MANDATORY COMMENTS VALIDATION - Check all mandatory requirements have comments
    const missingComments = mandatoryEvaluations.filter(evaluation => 
      !evaluation.comments || evaluation.comments.trim() === ''
    );
    
    if (missingComments.length > 0) {
      showNotification('error', `Comments are mandatory for all evaluations. Please provide justification for ${missingComments.length} requirement(s).`);
      return;
    }

    const allPass = mandatoryEvaluations.every(evaluation => evaluation.is_compliant);

    if (!allPass) {
      const failCount = mandatoryEvaluations.filter(evaluation => !evaluation.is_compliant).length;
      // Using a custom modal is better, but window.confirm is simple for now
      const confirmed = true; // Assume confirmation for non-interactive environments
      if (!confirmed) {
        return;
      }
    }

    const saveSuccess = await sendEvaluationToBackend(false);
    if (saveSuccess) {
      setCurrentStep('evaluation');
    }
  };

  const completeEvaluation = async () => {
    // Validate all comments are provided before completion
    if (!areAllCriteriaScored()) {
      const missingComments: string[] = [];
      
      mainCriteria.forEach((criterion, critIndex) => {
        criterion.sub_criteria.forEach((subCriterion) => {
          const evaluation = subCriterionEvaluations.find(e => e.sub_criterion_id === subCriterion.id);
          if (!evaluation?.score || !evaluation?.comments || evaluation.comments.trim() === '') {
            missingComments.push(`${criterion.name} → ${subCriterion.name}`);
          }
        });
      });
      
      const message = `Cannot complete evaluation. Missing comments for:\n\n${missingComments.slice(0, 5).join('\n')}${missingComments.length > 5 ? `\n\n...and ${missingComments.length - 5} more criteria` : ''}\n\nAll scores and detailed justification comments are mandatory before completion.`;
      
      alert(message);
      return;
    }
    
    const saveSuccess = await sendEvaluationToBackend(true);
    if (saveSuccess) {
      showNotification('success', 'Evaluation completed successfully!');
      setTimeout(() => {
        onComplete();
      }, 2000);
    }
  };

  const getCurrentSubCriterion = useCallback(() => {
    if (currentCriterionIndex >= mainCriteria.length) return null;
    const criterion = mainCriteria[currentCriterionIndex];
    if (!criterion || currentSubCriterionIndex >= criterion.sub_criteria.length) return null;
    return criterion.sub_criteria[currentSubCriterionIndex];
  }, [currentCriterionIndex, currentSubCriterionIndex, mainCriteria]);

  // Toggle criterion expansion
  const toggleCriterionExpansion = useCallback((criterionIndex: number) => {
    setExpandedCriteria(prev => {
      const newSet = new Set(prev);
      if (newSet.has(criterionIndex)) {
        newSet.delete(criterionIndex);
      } else {
        newSet.add(criterionIndex);
      }
      return newSet;
    });
  }, []);

  // Toggle sub-criterion expansion
  const toggleSubCriterionExpansion = useCallback((subCriterionId: number) => {
    setExpandedSubCriteria(prev => {
      const newSet = new Set(prev);
      const key = subCriterionId.toString();
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  // Auto-expand next criterion when current is completed
  const autoExpandNextCriterion = useCallback((currentIndex: number) => {
    if (currentIndex + 1 < mainCriteria.length) {
      setExpandedCriteria(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentIndex); // Collapse current
        newSet.add(currentIndex + 1); // Expand next
        return newSet;
      });
    }
  }, [mainCriteria.length]);

  // Check if criterion is completed (all sub-criteria scored AND commented)
  const isCriterionCompleted = useCallback((criterionIndex: number) => {
    const criterion = mainCriteria[criterionIndex];
    if (!criterion) return false;
    
    
    if (!criterion.sub_criteria || criterion.sub_criteria.length === 0) {
      return false;
    }
    
    const result = criterion.sub_criteria.every(subCrit => {
      const evaluation = subCriterionEvaluations.find(evaluation => evaluation.sub_criterion_id === subCrit.id);
      const isComplete = evaluation && 
             evaluation.score !== null && 
             evaluation.comments && 
             evaluation.comments.trim() !== '';
      if (evaluation) {
      } else {
      }
      return isComplete;
    });
    
    return result;
  }, [mainCriteria, subCriterionEvaluations]);

  // Calculate weighted average score for a main criterion
  const calculateCriterionAverageScore = useCallback((criterionIndex: number) => {
    const criterion = mainCriteria[criterionIndex];
    if (!criterion) return null;
    
    let totalWeightedScore = 0;
    let totalWeight = 0;
    let hasAnyScore = false;
    
    criterion.sub_criteria.forEach(subCrit => {
      const evaluation = subCriterionEvaluations.find(evaluation => evaluation.sub_criterion_id === subCrit.id);
      if (evaluation && evaluation.score !== null) {
        totalWeightedScore += evaluation.score * (subCrit.weight_percentage / 100);
        totalWeight += subCrit.weight_percentage / 100;
        hasAnyScore = true;
      }
    });
    
    return hasAnyScore && totalWeight > 0 ? totalWeightedScore / totalWeight : null;
  }, [mainCriteria, subCriterionEvaluations]);

  // Check if all criteria are completely scored
  const areAllCriteriaScored = useCallback(() => {
    return mainCriteria.every((criterion, index) => isCriterionCompleted(index));
  }, [mainCriteria, isCriterionCompleted]);



  const getProgressPercentage = useCallback(() => {
    if (currentStep === 'mandatory' || !mainCriteria.length) return 0;
    let totalSubCriteria = 0;
    let completedSubCriteria = 0;
    mainCriteria.forEach((criterion, critIndex) => {
      criterion.sub_criteria.forEach((sub, subIndex) => {
        totalSubCriteria++;
        if (critIndex < currentCriterionIndex ||
            (critIndex === currentCriterionIndex && subIndex <= currentSubCriterionIndex)) {
          completedSubCriteria++;
        }
      });
    });
    return totalSubCriteria > 0 ? (completedSubCriteria / totalSubCriteria) * 100 : 0;
  }, [currentStep, currentCriterionIndex, currentSubCriterionIndex, mainCriteria]);


  const getScoreDefinitions = useCallback(() => {
    return process?.score_scale === "0-10" ? scoreDefinitions_0_10 : scoreDefinitions_0_5;
  }, [process?.score_scale]);

  const getMaxScore = useCallback(() => {
    return process?.score_scale === "0-10" ? 10 : 5;
  }, [process?.score_scale]);


  // --- Render Logic ---
    if (isLoading) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={styles.contentWrapper}>
          <h1 style={styles.pageTitle}>Loading Evaluation Data...</h1>
          <div style={{ textAlign: 'center' }}>
              <div style={{...styles.card, maxWidth: '400px', margin: 'auto'}}>
                  <div style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}>Please wait...</div>
              </div>
          </div>
        </div>
      </div>
    );
  }

  if (!process || !supplier) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={styles.contentWrapper}>
          <h1 style={styles.pageTitle}>Error</h1>
          <div style={{ textAlign: 'center' }}>
              <div style={{...styles.card, maxWidth: '500px', margin: 'auto'}}>
                  <h3 style={{color: 'var(--color-error)'}}>Error: Process or Supplier data not found.</h3>
                  <button onClick={onBack} style={{...styles.button, marginTop: 'var(--spacing-md)'}}>← Back to Dashboard</button>
              </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer} className="density-compact">
      {notification && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000,
          background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)',
          color: 'white', padding: '1rem 1.5rem', borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        }}>
          {notification.message}
        </div>
      )}

      <div style={styles.contentWrapper}>
        <h1 style={styles.pageTitle}>Evaluating: {supplier.name}</h1>
        <p style={{ textAlign: 'center', color: 'var(--color-text-on-gradient-secondary)', marginBottom: 'var(--spacing-xl)' }}>
          {process.name}
          {lastSaved && (
            <span style={{ marginLeft: 'var(--spacing-md)', color: 'var(--color-success)', fontWeight: 'var(--font-weight-bold)' }}>
              • Last saved: {lastSaved.toLocaleTimeString()}
            </span>
          )}
        </p>

        {currentStep === 'evaluation' && (
          <div style={{ background: 'rgba(255,255,255,0.6)', padding: '1rem 2rem', borderBottom: `1px solid #e2e8f0`, marginBottom: 'var(--spacing-lg)' }}>
            <div style={{ background: '#e5e7eb', height: '12px', borderRadius: '9999px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(45deg, #2563eb, #059669)',
                height: '100%',
                borderRadius: '9999px',
                width: `${getProgressPercentage()}%`,
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        )}

        <main style={{ padding: '2rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          {currentStep === 'mandatory' && (
            <div style={styles.card}>
              <h2 style={{ color: '#1e293b', marginBottom: '1rem', fontSize: '1.5rem', fontWeight: 'bold' }}>Mandatory Requirements</h2>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '1rem', marginBottom: '2rem' }}>
                <p style={{ margin: 0, color: '#92400e', fontWeight: 'bold' }}>📋 Instructions:</p>
                <p style={{ margin: '0.5rem 0 0 0', color: '#92400e', fontSize: '0.9rem' }}>
                  Fields marked with <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span> and red borders are mandatory. 
                  All evaluation decisions must include detailed justification comments before you can proceed.
                </p>
              </div>
              <p style={{ color: '#6b7280', marginBottom: '2rem' }}>Evaluate whether {supplier.name} meets each mandatory requirement.</p>
              {mandatoryRequirements.map((requirement, index) => {
                const evaluation = mandatoryEvaluations.find(evalItem => evalItem.requirement_id === requirement.id);
                if (!evaluation) return null;
                return (
                  <div key={requirement.id} style={{
                    border: `1px solid #e2e8f0`, borderRadius: '12px', padding: '1.5rem',
                    marginBottom: '1rem', background: evaluation.is_compliant ? '#f9fafb' : '#fef2f2'
                  }}>
                    <p style={{ margin: '0 0 1rem 0', fontWeight: 'bold' }}>{index + 1}. {requirement.description}</p>
                    {/* --- REPLACEMENT START --- */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                        <button
                          onClick={() => updateMandatoryEvaluation(requirement.id, 'is_compliant', true)}
                          style={{
                            ...styles.compliantButton,
                            ...(evaluation.is_compliant ? styles.compliantButtonActive : styles.buttonInactive)
                          }}
                        >
                          ✅ Compliant
                        </button>
                        <button
                          onClick={() => updateMandatoryEvaluation(requirement.id, 'is_compliant', false)}
                          style={{
                            ...styles.compliantButton,
                            ...(!evaluation.is_compliant ? styles.nonCompliantButtonActive : styles.buttonInactive)
                          }}
                        >
                          ❌ Non-Compliant
                        </button>
                    </div>
                    {/* --- REPLACEMENT END --- */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold', 
                        color: '#374151' 
                      }}>
                        Comments & Justification: <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <textarea 
                        value={evaluation.comments || ''} 
                        onChange={(e) => updateMandatoryEvaluation(requirement.id, 'comments', e.target.value)} 
                        placeholder="Provide detailed justification for your compliance decision (required)..." 
                        rows={3} 
                        style={{
                          ...styles.input, 
                          fontSize: '0.9rem', 
                          padding: '12px',
                          border: (!evaluation.comments || evaluation.comments.trim() === '') 
                            ? '2px solid #fecaca' 
                            : '2px solid #d1fae5'
                        }} 
                      />
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
                <button 
                  type="button" 
                  onClick={async () => {
                    const success = await sendEvaluationToBackend(false);
                    if (success) {
                      showNotification('success', 'Progress saved successfully!');
                      setTimeout(() => onBack(), 1500);
                    }
                  }} 
                  disabled={isSaving} 
                  style={{...styles.button, background: 'linear-gradient(45deg, #f59e0b, #f97316)', ...((isSaving) && { background: '#9ca3af', cursor: 'not-allowed' })}}
                >
                  💾 Save & Close
                </button>
                <button type="button" onClick={saveMandatoryEvaluations} disabled={isSaving} style={{...styles.button, ...((isSaving) && { background: '#9ca3af', cursor: 'not-allowed' })}}>
                  {isSaving ? 'Saving...' : 'Continue to Detailed Evaluation →'}
                </button>
              </div>
            </div>
          )}

          {currentStep === 'evaluation' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Instructions for Evaluation Step */}
              <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                <p style={{ margin: 0, color: '#dc2626', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⚠️ Mandatory Comments Required
                </p>
                <p style={{ margin: '0.5rem 0 0 0', color: '#dc2626', fontSize: '0.9rem' }}>
                  <strong>Every criterion must have both a score AND detailed justification comments.</strong><br/>
                  Comment boxes show: 🔴 Red border = missing • 🟢 Green border = completed
                </p>
                <div style={{ 
                  background: 'rgba(220, 38, 38, 0.1)', 
                  border: '1px solid #fca5a5', 
                  borderRadius: '4px', 
                  padding: '0.5rem', 
                  marginTop: '0.5rem',
                  fontSize: '0.85rem',
                  color: '#991b1b'
                }}>
                  💡 <strong>Tip:</strong> Explain your reasoning, consider alternatives, and justify why you selected this score.
                </div>
              </div>
              {/* Hierarchical Criteria Display */}
              {mainCriteria.map((criterion, criterionIndex) => {
                const isExpanded = expandedCriteria.has(criterionIndex);
                const isCompleted = isCriterionCompleted(criterionIndex);
                const isCurrent = criterionIndex === currentCriterionIndex;
                
                return (
                  <div key={criterion.id} style={{
                    border: `2px solid ${isCurrent ? '#2563eb' : isCompleted ? '#10b981' : '#e2e8f0'}`,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    background: 'white'
                  }}>
                    {/* Criterion Header */}
                    <div 
                      onClick={() => toggleCriterionExpansion(criterionIndex)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1rem 1.5rem',
                        background: isCurrent ? '#eff6ff' : isCompleted ? '#f0fdf4' : '#f8fafc',
                        cursor: 'pointer',
                        borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{
                          fontSize: '1.2rem',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }}>
                          ▶
                        </span>
                        <h3 style={{ 
                          margin: 0, 
                          color: isCurrent ? '#2563eb' : isCompleted ? '#059669' : '#1e293b',
                          fontWeight: 'bold',
                          fontSize: '1.2rem'
                        }}>
                          {criterion.name}
                        </h3>
                        {isCompleted && (
                          <span style={{ color: '#10b981', fontSize: '1.2rem' }}>✅</span>
                        )}
                        {/* Always show score for ALL main criteria (whether they have sub-criteria or not) */}
                        {(() => {
                          const avgScore = calculateCriterionAverageScore(criterionIndex);
                          if (avgScore === null) return null;
                          
                          // For main criteria with multiple sub-criteria, show average
                          if (criterion.sub_criteria.length > 1) {
                            const scoredSubCriteria = criterion.sub_criteria.filter(subCrit => {
                              const evaluation = subCriterionEvaluations.find(evaluation => evaluation.sub_criterion_id === subCrit.id);
                              return evaluation && evaluation.score !== null;
                            }).length;
                            
                            return (
                              <span style={{
                                background: isCompleted ? '#10b981' : '#f59e0b',
                                color: 'white',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                marginLeft: '0.5rem'
                              }}>
                                Avg: {avgScore.toFixed(1)} ({scoredSubCriteria}/{criterion.sub_criteria.length})
                              </span>
                            );
                          } else {
                            // For main criteria without sub-criteria (synthetic sub-criteria), show direct score
                            return (
                              <span style={{
                                background: isCompleted ? '#10b981' : '#f59e0b',
                                color: 'white',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                marginLeft: '0.5rem'
                              }}>
                                Score: {avgScore.toFixed(1)}
                              </span>
                            );
                          }
                        })()}
                      </div>
                      <span style={{
                        background: isCurrent ? '#2563eb' : isCompleted ? '#10b981' : '#6b7280',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '9999px',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}>
                        Weight: {criterion.weight_percentage}%
                      </span>
                    </div>

                    {/* Criterion Content - Collapsible */}
                    {isExpanded && (
                      <div style={{ padding: '1.5rem' }}>
                        {criterion.description && (
                          <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                            {criterion.description}
                          </p>
                        )}
                        
                        
                        {/* Handle synthetic sub-criteria (Main 2, Main 3) differently than real sub-criteria (Main 1) */}
                        {criterion.sub_criteria.length === 1 && criterion.sub_criteria[0].id === criterion.id ? (
                          // Synthetic sub-criterion - render scoring interface directly inline
                          (() => {
                            const subCriterion = criterion.sub_criteria[0];
                            const evaluation = subCriterionEvaluations.find(evaluation => evaluation.sub_criterion_id === subCriterion.id);
                            
                            return (
                              <div style={{ marginTop: '1rem' }}>
                                <p style={{ 
                                  color: '#6b7280', 
                                  fontSize: '0.9rem', 
                                  fontStyle: 'italic',
                                  marginBottom: '1.5rem'
                                }}>
                                  This main criterion has no sub-criteria, so you'll score it directly.
                                </p>
                                
                                {/* Score Selection */}
                                <div style={{ marginBottom: '1rem' }}>
                                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#374151' }}>
                                    Score ({process.score_scale} Scale):
                                  </label>
                                  <div role="radiogroup" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
                                    gap: '0.5rem',
                                    marginBottom: '1rem'
                                  }}>
                                    {getScoreDefinitions().map(({ score }) => {
                                      const isSelected = evaluation?.score === score;
                                      return (
                                        <button
                                          key={score}
                                          type="button"
                                          role="radio"
                                          aria-checked={isSelected}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateSubCriterionEvaluation(subCriterion.id, 'score', score);
                                          }}
                                          style={{
                                            padding: '0.75rem 0.5rem',
                                            border: isSelected ? '3px solid #2563eb' : '2px solid #d1d5db',
                                            borderRadius: '8px',
                                            background: isSelected ? '#eff6ff' : 'white',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            fontSize: '0.9rem',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {score}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Score definition */}
                                {evaluation?.score !== null && evaluation && (
                                  <div style={{
                                    marginBottom: '1rem',
                                    padding: '0.75rem',
                                    background: '#eff6ff',
                                    border: '1px solid #2563eb',
                                    borderRadius: '6px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                      <span style={{ fontSize: '1rem', marginRight: '0.5rem' }}>✓</span>
                                      <strong style={{ color: '#2563eb' }}>Score {evaluation.score}: {getScoreDefinitions().find(def => def.score === evaluation.score)?.title}</strong>
                                    </div>
                                    <p style={{ margin: 0, color: '#1e40af', fontSize: '0.9rem' }}>
                                      {getScoreDefinitions().find(def => def.score === evaluation.score)?.description}
                                    </p>
                                  </div>
                                )}

                                {/* Comments Section */}
                                <div style={{ marginBottom: '1rem' }}>
                                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#374151' }}>
                                    Comments & Justification: <span style={{ color: '#ef4444' }}>*</span>
                                  </label>
                                  <textarea
                                    value={evaluation?.comments || ''}
                                    onChange={(e) => updateSubCriterionEvaluation(subCriterion.id, 'comments', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    placeholder="Provide detailed justification for your score..."
                                    style={{
                                      width: '100%',
                                      minHeight: '100px',
                                      padding: '0.75rem',
                                      border: '2px solid #d1d5db',
                                      borderRadius: '8px',
                                      fontSize: '0.9rem',
                                      resize: 'vertical',
                                      fontFamily: 'inherit'
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          // Real sub-criteria - render as collapsible sections
                          criterion.sub_criteria.map((subCriterion, subIndex) => {
                            const evaluation = subCriterionEvaluations.find(evaluation => evaluation.sub_criterion_id === subCriterion.id);
                            const isCurrentSub = isCurrent && subIndex === currentSubCriterionIndex;
                            const isSubCompleted = evaluation && evaluation.score !== null;
                            const isSubExpanded = expandedSubCriteria.has(subCriterion.id.toString());
                          
                          return (
                            <div key={subCriterion.id} style={{
                              border: `1px solid ${isCurrentSub ? '#2563eb' : isSubCompleted ? '#10b981' : '#e2e8f0'}`,
                              borderRadius: '8px',
                              marginBottom: '1rem',
                              background: 'white',
                              transition: 'all 0.3s ease'
                            }}>
                              {/* Sub-criterion header - clickable to expand/collapse */}
                              <div 
                                onClick={() => toggleSubCriterionExpansion(subCriterion.id)}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '1rem',
                                  cursor: 'pointer',
                                  background: isCurrentSub ? '#eff6ff' : isSubCompleted ? '#f0fdf4' : '#fafafa',
                                  borderBottom: isSubExpanded ? '1px solid #e2e8f0' : 'none'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                  <span style={{
                                    fontSize: '1rem',
                                    transform: isSubExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                  }}>
                                    ▶
                                  </span>
                                  <h4 style={{
                                    margin: 0,
                                    color: isCurrentSub ? '#2563eb' : '#1e293b',
                                    fontWeight: '600',
                                    fontSize: '1rem'
                                  }}>
                                    {subCriterion.name}
                                  </h4>
                                  {isSubCompleted && (
                                    <span style={{ color: '#10b981', fontSize: '1rem' }}>✅</span>
                                  )}
                                </div>
                                <span style={{
                                  background: '#e0f2fe',
                                  color: '#0369a1',
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '9999px',
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold'
                                }}>
                                  {subCriterion.weight_percentage}%
                                </span>
                              </div>

                              {/* Sub-criterion content - only show when expanded */}
                              {isSubExpanded && (
                                <div style={{ padding: '1rem' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                <div style={{ flex: 1 }}>
                                  {/* For real sub-criteria, show the sub-criterion name */}
                                  <h4 style={{
                                    margin: '0 0 0.5rem 0',
                                    color: isCurrentSub ? '#2563eb' : '#1e293b',
                                    fontWeight: '600'
                                  }}>
                                    {subCriterion.name}
                                  </h4>
                                  {subCriterion.description && (
                                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
                                      {subCriterion.description}
                                    </p>
                                  )}
                                </div>
                                <span style={{
                                  background: '#e0f2fe',
                                  color: '#0369a1',
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '9999px',
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {subCriterion.weight_percentage}%
                                </span>
                              </div>

                              {/* Score Selection - Always show for expanded criteria to allow editing */}
                              {isExpanded && (
                                <div style={{ marginBottom: '1rem' }}>
                                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#374151' }}>
                                    Score ({process.score_scale} Scale):
                                  </label>
                                  <div role="radiogroup" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
                                    gap: '0.5rem',
                                    marginBottom: '1rem'
                                  }}>
                                    {getScoreDefinitions().map(({ score }) => {
                                      const isSelected = evaluation?.score === score;
                                      return (
                                        <button
                                          key={score}
                                          type="button"
                                          role="radio"
                                          aria-checked={isSelected}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateSubCriterionEvaluation(subCriterion.id, 'score', score);
                                          }}
                                          style={{
                                            padding: '0.75rem 0.5rem',
                                            border: isSelected ? '3px solid #2563eb' : '2px solid #d1d5db',
                                            borderRadius: '8px',
                                            background: isSelected ? '#eff6ff' : 'white',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            color: isSelected ? '#2563eb' : '#374151',
                                            transition: 'all 0.2s ease',
                                            fontSize: '1rem'
                                          }}
                                        >
                                          {score}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  
                                  {/* Always show selected score description */}
                                  {evaluation && evaluation.score !== null && (
                                    <div style={{
                                      background: '#e0f2fe',
                                      padding: '0.75rem',
                                      borderRadius: '8px',
                                      border: '2px solid #0369a1',
                                      marginBottom: '1rem'
                                    }}>
                                      {(() => {
                                        const scoreDef = getScoreDefinitions().find(def => def.score === evaluation.score);
                                        return scoreDef ? (
                                          <>
                                            <p style={{ margin: 0, fontWeight: 'bold', color: '#0c4a6e', fontSize: '0.9rem' }}>
                                              ✓ Score {scoreDef.score}: {scoreDef.title}
                                            </p>
                                            <p style={{ margin: '0.25rem 0 0 0', color: '#075985', fontSize: '0.8rem' }}>
                                              {scoreDef.description}
                                            </p>
                                          </>
                                        ) : null;
                                      })()}
                                    </div>
                                  )}
                                  
                                  {/* Score reference guide - always visible */}
                                  <details style={{ marginBottom: '1rem' }}>
                                    <summary style={{
                                      cursor: 'pointer',
                                      fontWeight: 'bold',
                                      color: '#374151',
                                      fontSize: '0.9rem',
                                      padding: '0.5rem',
                                      background: '#f9fafb',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '6px'
                                    }}>
                                      📋 Score Reference Guide ({process.score_scale} Scale)
                                    </summary>
                                    <div style={{
                                      marginTop: '0.5rem',
                                      padding: '1rem',
                                      background: '#fafafa',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '6px',
                                      maxHeight: '300px',
                                      overflowY: 'auto'
                                    }}>
                                      {getScoreDefinitions().map(scoreDef => (
                                        <div key={scoreDef.score} style={{
                                          marginBottom: '1rem',
                                          paddingBottom: '1rem',
                                          borderBottom: scoreDef.score === getScoreDefinitions().length - 1 ? 'none' : '1px solid #e5e7eb'
                                        }}>
                                          <p style={{ 
                                            margin: '0 0 0.25rem 0', 
                                            fontWeight: 'bold', 
                                            color: '#1e293b',
                                            fontSize: '0.85rem'
                                          }}>
                                            Score {scoreDef.score}: {scoreDef.title}
                                          </p>
                                          <p style={{ 
                                            margin: 0, 
                                            color: '#6b7280', 
                                            fontSize: '0.8rem',
                                            lineHeight: '1.4'
                                          }}>
                                            {scoreDef.description}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </details>

                                  <label htmlFor={`comments-${subCriterion.id}`} style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 'bold',
                                    color: '#374151'
                                  }}>
                                    Comments & Justification: <span style={{ color: '#ef4444' }}>*</span>
                                  </label>
                                  <textarea
                                    id={`comments-${subCriterion.id}`}
                                    value={evaluation?.comments || ''}
                                    onChange={(e) => updateSubCriterionEvaluation(subCriterion.id, 'comments', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    placeholder="Provide detailed comments justifying your score (required)..."
                                    rows={3}
                                    style={{
                                      width: '100%',
                                      padding: '0.75rem',
                                      border: (!evaluation?.comments || evaluation.comments.trim() === '') 
                                        ? '2px solid #fecaca' 
                                        : '2px solid #d1fae5',
                                      borderRadius: '8px',
                                      fontSize: '0.9rem',
                                      resize: 'vertical'
                                    }}
                                  />
                                </div>
                              )}

                              {/* Show score summary for completed sub-criteria */}
                              {!isCurrentSub && isSubCompleted && (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '0.5rem',
                                  background: '#f0fdf4',
                                  borderRadius: '6px',
                                  border: '1px solid #bbf7d0'
                                }}>
                                  <span style={{ color: '#059669', fontWeight: 'bold' }}>
                                    ✅ Score: {evaluation?.score}/{process.score_scale === '0-10' ? '10' : '5'}
                                  </span>
                                  <button
                                    onClick={() => {
                                      setCurrentCriterionIndex(criterionIndex);
                                      setCurrentSubCriterionIndex(subIndex);
                                    }}
                                    style={{
                                      background: 'transparent',
                                      border: '1px solid #059669',
                                      color: '#059669',
                                      padding: '0.25rem 0.75rem',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.8rem'
                                    }}
                                  >
                                    Edit
                                  </button>
                                </div>
                              )}
                                </div>
                              )}
                            </div>
                          );
                        })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Evaluation Controls */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.5rem',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                marginTop: '1rem'
              }}>
                <button
                  type="button"
                  onClick={() => sendEvaluationToBackend(false)}
                  disabled={isSaving}
                  style={{
                    ...styles.button,
                    background: 'linear-gradient(45deg, #f59e0b, #f97316)',
                    ...((isSaving) && { background: '#9ca3af', cursor: 'not-allowed' })
                  }}
                >
                  {isSaving ? 'Saving...' : '💾 Save Progress'}
                </button>
                
                <button
                  type="button"
                  onClick={completeEvaluation}
                  disabled={isSaving || !areAllCriteriaScored()}
                  style={{
                    ...styles.button,
                    background: areAllCriteriaScored() 
                      ? 'linear-gradient(45deg, #10b981, #059669)' 
                      : '#d1d5db',
                    color: areAllCriteriaScored() ? 'white' : '#6b7280',
                    border: areAllCriteriaScored() 
                      ? '2px solid #10b981' 
                      : '2px solid #d1d5db',
                    cursor: (isSaving || !areAllCriteriaScored()) ? 'not-allowed' : 'pointer'
                  }}
                  title={!areAllCriteriaScored() ? 'Complete all criteria to enable this button' : 'Complete and finalize this evaluation'}
                >
                  {isSaving ? 'Saving...' : areAllCriteriaScored() ? 'Complete Evaluation ✅' : 'Complete All Criteria First'}
                </button>
              </div>
            </div>
          )}

          {currentStep === 'evaluation' && (
            <div style={{
              background: '#fffbeb', padding: '1rem', borderRadius: '12px',
              border: `1px solid #fde68a`, marginTop: '2rem'
            }}>
              <p style={{ margin: 0, color: '#92400e', fontWeight: 'bold' }}>💡 Anti-Fatigue Features Active:</p>
              <p style={{ margin: '0.5rem 0 0 0', color: '#92400e', fontSize: '0.9rem' }}>
                Auto-save every 30 seconds • Smart navigation • Progress tracking • {mandatoryEvaluations.filter(e => e.is_compliant).length}/{mandatoryRequirements.length} mandatory requirements passed
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
    </div>
  );
};

export default EvaluationInterface;
